import { formatPriceLak } from "@/shared/lib/format-price";
import logo from "../../../../../assets/favicon_io/android-chrome-192x192.png";
import {
  formatPrintBillLocationParts,
  formatPrintVariantOptions,
  type OrderPrintData,
} from "../../utils/print-types";

interface ShippingLabelPrintViewProps {
  order: OrderPrintData;
}

// Brand configuration - can be moved to settings later
const BRAND_NAME = "Freedom";
const BRAND_TAGLINE = "Thank you / Hope to see you again";
// Use absolute path for logo in print window (favicon_io)
const BRAND_LOGO = logo;

/** Max line items per 72×100mm label; overflow shown as a note. COD: real total for COD, 0 for online (paid upstream). */
const SHIPPING_LABEL_MAX_ITEMS = 5;

function joinPipeParts(parts: Array<string | null | undefined>): string {
  return parts
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" | ");
}

function formatItemOptions(
  item: OrderPrintData["items"][number],
): string | null {
  return formatPrintVariantOptions(item.resolvedVariantOptions);
}

export function ShippingLabelPrintView({ order }: ShippingLabelPrintViewProps) {
  const payment = order.paymentMethod;
  const showItemPrices = payment === "cod" || payment === "online";
  const showLuumPayThangRow = payment === "cod" || payment === "online";
  const luumPayThangAmount = payment === "cod" ? order.grandTotal : 0;
  const storePhone = order.contactPhone?.trim();

  const recipientName =
    order.shipping?.firstName?.trim() || order.customer?.name?.trim() || "-";

  /** ຊື່ຜູ້ຮັບ | ຂ.ແຂວງ | ມ.ເມືອງ | ບ.ບ້ານ | ຜູ້ຂົນສົ່ງ | ສາຂາ */
  const destinationPipe = joinPipeParts([
    recipientName !== "-" ? recipientName : null,
    ...formatPrintBillLocationParts(order.shipping),
    order.shipping?.shippingCarrierName,
    order.shipping?.carrierBranch,
  ]);

  const recipientPhone = order.shipping?.phoneNumber ?? "-";
  const shipmentNumber =
    order.shipping?.shipmentNumber ?? order.shipping?.trackingNumber ?? null;

  const labelItems = order.items.slice(0, SHIPPING_LABEL_MAX_ITEMS);
  const extraItemCount = Math.max(
    0,
    order.items.length - SHIPPING_LABEL_MAX_ITEMS,
  );

  return (
    <div className="print-container shipping-label">
      <div className="shipping-label-main">
        <div className="print-header">
          <div className="brand-header-row">
            <div className="brand-logo">
              <img
                src={BRAND_LOGO}
                alt={BRAND_NAME}
                className="logo-image"
                onError={(e) => {
                  // Hide image if it fails to load
                  (e.target as HTMLImageElement).style.display = "none";
                }}
              />
            </div>
            <div className="brand-text">
              <div className="brand-by">{BRAND_NAME}</div>
              <div className="brand-tagline">{BRAND_TAGLINE}</div>
            </div>
          </div>
        </div>
        <div className="print-divider" />
        <div className="print-section">
          <div className="contact-info sender-line shipping-label-sender-strip">
            ເບີຜູ້ສົ່ງ: {storePhone ?? "—"} · Order: {order.orderNumber}
          </div>
        </div>
        <div className="print-divider" />
        <div className="print-section">
          <div className="label-destination-line">{destinationPipe || "-"}</div>
          <div className="label-field label-field-tight">
            <span className="label-text">ເບີຜູ້ຮັບ:</span>
            <span className="label-value">{recipientPhone}</span>
          </div>
          {shipmentNumber ? (
            <div className="label-field label-field-tight label-total">
              <span className="label-text">Shipment No:</span>
              <span className="label-value">{shipmentNumber}</span>
            </div>
          ) : null}
          <div className="label-items-block">
            <div className="label-items-title">ລາຍການສິນຄ້າ:</div>
            {labelItems.map((item) => {
              const optsText = formatItemOptions(item);
              const unit = Number(item.variantPrice ?? item.price);
              return (
                <div key={item.id} className="label-item">
                  <div className="label-item-name">
                    {item.productName?.trim() || "—"}
                    {optsText ? ` · ${optsText}` : ""}
                  </div>
                  <div className="label-item-row">
                    <span>
                      ຈຳນວນ {item.quantity}
                      {showItemPrices ? (
                        <> · ລາຄາ {formatPriceLak(unit)}</>
                      ) : null}
                    </span>
                    {showItemPrices ? (
                      <span>ລວມ {formatPriceLak(item.total)}</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {extraItemCount > 0 ? (
              <div className="label-items-overflow">
                +ອີກ {extraItemCount} ລາຍການ — ເບິ່ງລະບົບ / ພິມເພີ່ມ
              </div>
            ) : null}
          </div>
        </div>
        {shipmentNumber ? (
          <>
            <div className="print-divider" />
            <div className="print-section">
              <div className="order-info">
                <div>Shipment No: {shipmentNumber}</div>
              </div>
            </div>
          </>
        ) : null}
      </div>
      <div className="shipping-label-footer-end">
        {showLuumPayThangRow ? (
          <div className="label-field label-field-tight label-total shipping-label-luum-pay-thang">
            <span className="label-text">COD:</span>
            <span className="label-value">
              {formatPriceLak(luumPayThangAmount)}
            </span>
          </div>
        ) : null}
        <div className="print-footer">ຂອບໃຈທີ່ໃຊ້ບໍລິການຂອງພວກເຮົາ</div>
      </div>
    </div>
  );
}
